import React from 'react'
import { HeadlessTableConfig, HeadlessTableServerState, HeadlessTableParams, HeadlessTableClientState } from './types'
import { applyTableStateParams, getParamName } from './get-table-state'

export function useTable<C extends HeadlessTableConfig<any, any, any, any>>(
  config: C,
  serverState: HeadlessTableServerState<C>
): HeadlessTableClientState<C> {
  const rowRef = React.useRef(serverState.rows)
  const [selectedIndices, setSelectedIndices] = React.useState<number[]>([])
  if (rowRef.current !== serverState.rows) {
    rowRef.current = serverState.rows
    if (selectedIndices.length > 0) {
      setSelectedIndices([])
    }
  }
  const clientQuickFiltering = !!serverState.clientQuickFilters.includes(
    (serverState.serverParams.quickFilter || serverState.defaultState.quickFilter) as string
  )
  const computedFilteringTier = serverState.filteringTier === 'client' || clientQuickFiltering ? 'client' : 'server'
  const [params, setParam] = React.useState<HeadlessTableParams<C>>({
    filters: serverState.serverParams.filters || serverState.defaultState?.filters || {},
    sort: serverState.serverParams.sort || serverState.defaultState?.sort || Object.keys(config.sorts)[0],
    limit: serverState.serverParams.limit || serverState.defaultState?.limit || 30,
    skip: serverState.serverParams.skip || serverState.defaultState?.skip || 0,
    search: decodeURIComponent(serverState.serverParams.search || serverState.defaultState?.search || ''),
    quickFilter: serverState.serverParams.quickFilter || (serverState.defaultState?.quickFilter as string) || null
  })

  const state = React.useMemo(() => {
    if (computedFilteringTier === 'server') {
      return { ...serverState.serverParams, rows: serverState.rows, rowCount: serverState.rowCount }
    } else {
      const applied = applyTableStateParams({
        config,
        rows: serverState.rows,
        params: clientQuickFiltering ? { ...params, quickFilter: null } : params
      })
      return { ...applied, ...params }
    }
  }, [config, serverState, params, clientQuickFiltering, computedFilteringTier])

  const toggleSelectRow = React.useCallback(
    (index: number) => {
      setSelectedIndices((previous) => {
        if (previous.includes(index)) {
          return previous.filter((p) => p !== index)
        } else {
          const next = [...previous, index]
          next.sort()
          return next
        }
      })
    },
    [setSelectedIndices]
  )
  const toggleSelectAll = React.useCallback(() => {
    setSelectedIndices((previous) => {
      if (previous.length === state.rows.length) {
        return []
      } else {
        return state.rows.map((r, i) => i)
      }
    })
  }, [state])

  const getURLSearchParams = React.useCallback(() => {
    const searchParams = new URLSearchParams()
    const prefix = config.prefix || ''

    const addToSearchParams = (key: string, value?: string | null, skipStringify = false) => {
      if (typeof value === 'string') {
        searchParams.set(getParamName(prefix, key), encodeURIComponent(!skipStringify ? JSON.stringify(value) : value))
      }
    }

    if (state.sort && serverState.defaultState.sort !== state.sort) {
      addToSearchParams('sort', String(state.sort))
    }
    if (state.quickFilter && serverState.defaultState.quickFilter !== state.quickFilter) {
      addToSearchParams('quickFilter', String(state.quickFilter))
    }

    if (state.search) {
      addToSearchParams('search', String(state.search), true)
    }

    if (state.skip && serverState.defaultState.skip !== state.skip) {
      addToSearchParams('skip', String(state.skip))
    }

    Object.entries(state.filters).forEach(
      ([key, value]) =>
        serverState.defaultState?.filters?.[key] !== state.filters[key] && addToSearchParams(key, value as string | null)
    )
    return searchParams
  }, [
    config.prefix,
    serverState.defaultState?.filters,
    serverState.defaultState.quickFilter,
    serverState.defaultState.skip,
    serverState.defaultState.sort,
    state.filters,
    state.quickFilter,
    state.search,
    state.skip,
    state.sort
  ])

  return {
    ...state,
    serverParams: serverState.serverParams,
    prefix: config.prefix || '',
    sortOptions: Object.keys(config.sorts),
    filterOptions: Object.keys(config.filters),
    quickFilterOptions: Object.keys(config.quickFilters || {}),
    defaultState: serverState.defaultState,
    getURLSearchParams,
    selectedIndices,
    toggleSelectAll,
    toggleSelectRow,
    setClientParam: setParam,
    filteringTier: serverState.filteringTier,
    clientQuickFilters: serverState.clientQuickFilters
  }
}
